import React, { useEffect, useState } from 'react';
import FormModal from '../../Modals/FormModal';
import CashClosureService from '../../../services/cash/CashClosureService';
import { formatCurrency } from '../../../utils/formatCurrency';

interface Props {
  closureId: number | null;
  onClose: () => void;
}

interface PaymentRow {
  code: string;
  description: string;
  amount: number;
}

interface DetailRow {
  denomination: number;
  quantity: number;
  subtotal: number;
}

interface ReportData {
  closureNumber: string;
  branchName: string;
  registerName: string;
  sessionNumber: string;
  openedBy: string;
  closedBy: string;
  openingTime: string;
  closureTime: string;
  totalSales: number;
  expectedCash: number;
  countedCash: number;
  difference: number;
  observations: string | null;
  payments: PaymentRow[];
  details: DetailRow[];
}

const CashClosureDetailModal: React.FC<Props> = ({ closureId, onClose }) => {
  const [report, setReport] = useState<ReportData | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!closureId) {
      setReport(null);
      return;
    }

    let cancelled = false;

    (async () => {
      setLoading(true);
      try {
        const response = await CashClosureService.getReport(closureId);
        if (!cancelled) setReport(response.data);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [closureId]);

  return (
    <FormModal
      isOpen={closureId !== null}
      onClose={onClose}
      title="Detalle del Corte Z"
      maxWidth="max-w-2xl"
    >
      {loading && (
        <div className="py-8 text-center text-sm text-body">Cargando...</div>
      )}

      {!loading && report && (
        <div className="flex flex-col gap-5">
          <div className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
            <div>
              <div className="text-xs font-semibold uppercase text-body">
                No. Cierre
              </div>
              <div className="font-medium text-black dark:text-white">
                {report.closureNumber}
              </div>
            </div>

            <div>
              <div className="text-xs font-semibold uppercase text-body">
                Sesión
              </div>
              <div className="font-medium text-black dark:text-white">
                {report.sessionNumber}
              </div>
            </div>

            <div>
              <div className="text-xs font-semibold uppercase text-body">
                Sucursal
              </div>
              <div className="font-medium text-black dark:text-white">
                {report.branchName}
              </div>
            </div>

            <div>
              <div className="text-xs font-semibold uppercase text-body">
                Caja
              </div>
              <div className="font-medium text-black dark:text-white">
                {report.registerName}
              </div>
            </div>

            <div>
              <div className="text-xs font-semibold uppercase text-body">
                Cajero
              </div>
              <div className="font-medium text-black dark:text-white">
                {report.openedBy}
              </div>
            </div>

            <div>
              <div className="text-xs font-semibold uppercase text-body">
                Cerrado por
              </div>
              <div className="font-medium text-black dark:text-white">
                {report.closedBy}
              </div>
            </div>

            <div>
              <div className="text-xs font-semibold uppercase text-body">
                Apertura
              </div>
              <div className="font-medium text-black dark:text-white">
                {new Date(report.openingTime).toLocaleString()}
              </div>
            </div>

            <div>
              <div className="text-xs font-semibold uppercase text-body">
                Cierre
              </div>
              <div className="font-medium text-black dark:text-white">
                {new Date(report.closureTime).toLocaleString()}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 rounded-xl border border-stroke p-4 dark:border-strokedark">
            <div>
              <div className="text-xs font-semibold uppercase text-body">
                Total Ventas
              </div>
              <div className="text-lg font-bold text-primary">
                ${formatCurrency(report.totalSales)}
              </div>
            </div>

            <div>
              <div className="text-xs font-semibold uppercase text-body">
                Efectivo Esperado
              </div>
              <div className="text-lg font-bold text-primary">
                ${formatCurrency(report.expectedCash)}
              </div>
            </div>

            <div>
              <div className="text-xs font-semibold uppercase text-body">
                Contado
              </div>
              <div className="font-medium text-black dark:text-white">
                ${formatCurrency(report.countedCash)}
              </div>
            </div>

            <div>
              <div className="text-xs font-semibold uppercase text-body">
                Diferencia
              </div>
              <div
                className={`font-medium ${
                  Number(report.difference) < 0
                    ? 'text-danger'
                    : 'text-black dark:text-white'
                }`}
              >
                ${formatCurrency(report.difference)}
              </div>
            </div>
          </div>

          {report.payments?.length > 0 && (
            <div>
              <div className="mb-2 text-sm font-semibold text-black dark:text-white">
                Métodos de Pago
              </div>
              <table className="w-full text-sm">
                <tbody>
                  {report.payments.map((p) => (
                    <tr
                      key={p.code}
                      className="border-b border-stroke last:border-0 dark:border-strokedark"
                    >
                      <td className="py-1.5">{p.description}</td>
                      <td className="py-1.5 text-right font-medium">
                        ${formatCurrency(p.amount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {report.details?.length > 0 && (
            <div>
              <div className="mb-2 text-sm font-semibold text-black dark:text-white">
                Denominaciones Contadas
              </div>
              <table className="w-full text-sm">
                <tbody>
                  {report.details.map((d, i) => (
                    <tr
                      key={i}
                      className="border-b border-stroke last:border-0 dark:border-strokedark"
                    >
                      <td className="py-1.5">
                        ${formatCurrency(d.denomination)}
                      </td>
                      <td className="py-1.5 text-center">x{d.quantity}</td>
                      <td className="py-1.5 text-right font-medium">
                        ${formatCurrency(d.subtotal)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {report.observations && (
            <div>
              <div className="mb-1 text-sm font-semibold text-black dark:text-white">
                Observaciones
              </div>
              <p className="text-sm text-body">{report.observations}</p>
            </div>
          )}
        </div>
      )}
    </FormModal>
  );
};

export default CashClosureDetailModal;
